import { difference } from 'lodash';
import { useGetters, useActions } from 'vuex-composition-helpers';

import {
  useMutationBook,
  useActionBook,
  useAppCommon,
  useSyncData,
  useMappingProject
} from '@/hooks';

import { isEmpty, isOk, isSecondaryFormat } from '@/common/utils';
import { GETTERS, ACTIONS } from '@/store/modules/digital/const';
import {
  mappingFrameConfigToApi,
  mappingFrameToApi
} from '@/common/mapping/frame';
import { saveDigitalConfigApi, saveDigitalObjectsApi } from '@/api/saveDigital';
import { uploadBase64ImageApi } from '@/api/util';
import { usePhotos } from '../../composables';
import { updateInProjectApiMultiple } from '@/api/savePrint';
import { useLoadStyleEditor } from '@/views/CreateBook/composables';

export const useSaveData = () => {
  const { getDataEditScreen, mediaObjectIds } = useGetters({
    getDataEditScreen: GETTERS.GET_DATA_EDIT_SCREEN,
    mediaObjectIds: GETTERS.GET_MEDIA_OBJECT_IDS
  });
  const { getInProjectAssets } = usePhotos();
  const { syncToPrint } = useSyncData();
  const { getMappingConfig } = useMappingProject();

  const saveEditScreen = async (data, elementMappings, isContentChange) => {
    if (isEmpty(data.frame)) return;

    const promise = [saveDigitalConfig(data)];
    isContentChange && promise.push(saveDigitalObjects(data, elementMappings));

    return Promise.all(promise);
  };

  /**
   * To save digital data to DB
   *
   * fields will be saved on frame:
   *  title
   *  frame_delay
   *  is_visited
   *  frame_order
   *
   * fileds saved on book
   *  default theme id
   *
   * @param {Object} editScreenData sheet data
   * @returns api response
   */
  const saveDigitalConfig = async editScreenData => {
    const { frame, defaultThemeId, bookId } = editScreenData;

    if (isEmpty(frame)) return;

    const variables = {
      bookId,
      bookParams: { digital_theme_id: parseInt(defaultThemeId) },
      frameId: frame.id,
      frameParams: mappingFrameConfigToApi(frame)
    };

    // update objects and other data
    return saveDigitalConfigApi(variables);
  };

  /**
   * To save digital data to DB
   *
   * fields will be saved on frame:
   *  objects
   *  preview_image_url
   *  play_in_ids
   *  play_out_ids
   *
   *
   * @param {Object} editScreenData sheet data
   * @returns api response
   */
  const saveDigitalObjects = async (editScreenData, elementMappings) => {
    const { frame, bookId, sheetId } = editScreenData;

    if (isEmpty(frame)) return;

    const imgUrl = await uploadBase64ImageApi(frame.previewImageUrl);
    frame.previewImageUrl = isOk(imgUrl) ? imgUrl.data : '';

    const variables = {
      frameId: frame.id,
      frameParams: mappingFrameToApi(frame)
    };

    const config = await getMappingConfig(bookId);
    const elementMappingIds = elementMappings
      .filter(element => element.mapped)
      .map(element => element.digitalElementId);
    const isSecondary = isSecondaryFormat(config, true);

    const projectId = +frame.id;
    const currentAssetIds = isSecondary
      ? frame.objects
          .filter(o => o.imageId && !elementMappingIds.includes(o.id))
          .map(o => o.imageId)
      : mediaObjectIds.value;
    const { apiPageAssetIds } = await getInProjectAssets(projectId);

    const addAssetIds = difference(currentAssetIds, apiPageAssetIds);

    const removeAssetIds = difference(apiPageAssetIds, currentAssetIds);

    const inProjectVariables = {
      bookId: +bookId,
      projectId,
      addAssetIds,
      removeAssetIds
    };

    // update in project mark of assets
    const isSuccessOfInProject = await updateInProjectApiMultiple(
      inProjectVariables,
      true
    );

    // update objects and other data
    const isSuccess = await saveDigitalObjectsApi(variables);

    await syncToPrint(sheetId, frame, elementMappings);

    return isSuccess && isSuccessOfInProject;
  };

  return {
    saveEditScreen,
    getDataEditScreen
  };
};

export const useObject = () => {
  const { updateObjectsToStore } = useActions({
    updateObjectsToStore: ACTIONS.UPDATE_OBJECTS_TO_STORE
  });

  return { updateObjectsToStore };
};

export const useBookDigitalInfo = () => {
  const { setBookInfo, setSectionsSheets } = useMutationBook(true);

  const { setGeneralInfo } = useAppCommon();
  const { loadStyles } = useLoadStyleEditor();

  const { getBookInfo } = useActionBook();

  const getBookDigitalInfo = async bookId => {
    const { book: digitalMain, sections, sheets } = await getBookInfo(bookId);
    const { book } = await getBookInfo(bookId, true);

    setSectionsSheets({ sections, sheets });

    const { communityId, title } = digitalMain;
    const { themeId, isPhotoVisited, bookUserId } = book;
    setBookInfo({
      info: {
        id: bookId,
        communityId,
        defaultThemeId: themeId,
        isPhotoVisited,
        bookUserId
      }
    });

    setGeneralInfo({ info: { bookId, title } });
    await loadStyles();
  };

  return {
    getBookDigitalInfo
  };
};

export const useVideo = () => {
  const { totalVideoDuration } = useGetters({
    totalVideoDuration: GETTERS.GET_TOTAL_VIDEO_DURATION
  });

  return { totalVideoDuration };
};
